'use client';

import { c as _c } from "react/compiler-runtime";
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { FileText, GitBranch, Headphones } from 'lucide-react';
import { Badge, Button } from '@/shared/components/ui';
import { formatCustomFieldValue } from '@/features/negociacaoColunas';
import { formatCpfCnpj } from '@/shared/utils/cnpj';
import { FeatherActivity, FeatherClock, FeatherMessageSquare, FeatherUser } from '@subframe/core';
import { getSituacaoTags } from '../../utils/situacaoTags';
import { NegociacaoTemperatureRow } from '../common/NegociacaoTemperature';
import type { NegociacaoDetailSidebarProps } from './NegociacaoDetailSidebar';
import { NegociacaoDetailFlowSection } from './NegociacaoDetailFlowSection';
import { NegociacaoDetailTelefones } from './NegociacaoDetailTelefones';
interface NegociacaoDetailSidebarContentProps extends NegociacaoDetailSidebarProps {
  section: string;
  onSectionChange: (value: string) => void;
}
const sections = [{
  value: 'dados',
  label: 'Dados',
  icon: FileText
}, {
  value: 'atendimento',
  label: 'Atendimento',
  icon: Headphones
}, {
  value: 'automacao',
  label: 'Automação',
  icon: GitBranch
}] as const;
export function NegociacaoDetailSidebarContent(t0) {
  const $ = _c(170);
  const {
    negotiation,
    colunasExtras,
    flow,
    isFlowLoading,
    iaStatusUi,
    isIAPaused,
    isReadOnly,
    canToggleAutomation,
    isToggleAutomationLoading,
    isStartPending,
    isStartFailed,
    isStartCancelled,
    agentInput,
    onToggleAutomationClick,
    getTemperatureColorClass,
    situacoes,
    telefones,
    telefonesLoading,
    telefonesError,
    onCarregarTelefones,
    section,
    onSectionChange
  } = t0;
  let T0;
  let T1;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== colunasExtras || $[1] !== getTemperatureColorClass || $[2] !== iaStatusUi.dotClass || $[3] !== iaStatusUi.message || $[4] !== iaStatusUi.title || $[5] !== negotiation.agent || $[6] !== negotiation.channel || $[7] !== negotiation.client || $[8] !== negotiation.confidence || $[9] !== negotiation.confidenceLevel || $[10] !== negotiation.counterpartyCpfCnpj || $[11] !== negotiation.counterpartyPhone || $[12] !== negotiation.createdAt || $[13] !== negotiation.customFieldValues || $[14] !== negotiation.negotiationName || $[15] !== negotiation.numeroNegociacao || $[16] !== negotiation.status || $[17] !== negotiation.statusVariant || $[18] !== negotiation.temperature || $[19] !== onCarregarTelefones || $[20] !== onSectionChange || $[21] !== section || $[22] !== situacoes || $[23] !== telefones || $[24] !== telefonesError || $[25] !== telefonesLoading) {
    const situacaoTags = getSituacaoTags(situacoes);
    T1 = TabsPrimitive.Root;
    t13 = section;
    t14 = onSectionChange;
    t15 = "flex h-full min-h-0 w-full flex-col bg-white break-words";
    let t18;
    if ($[45] !== negotiation.negotiationName) {
      t18 = <span className="text-default-font text-sm leading-snug font-semibold">{negotiation.negotiationName}</span>;
      $[45] = negotiation.negotiationName;
      $[46] = t18;
    } else {
      t18 = $[46];
    }
    let t19;
    if ($[47] !== negotiation.status || $[48] !== negotiation.statusVariant) {
      t19 = <Badge variant={negotiation.statusVariant}>{negotiation.status}</Badge>;
      $[47] = negotiation.status;
      $[48] = negotiation.statusVariant;
      $[49] = t19;
    } else {
      t19 = $[49];
    }
    const t20 = <div className="flex flex-col gap-1.5">{t18}<div className="flex flex-wrap items-center gap-1.5">{t19}{situacaoTags.map(_temp)}</div></div>;
    let t21;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <TabsPrimitive.List aria-label={"Se\xE7\xF5es dos detalhes da negocia\xE7\xE3o"} className="mt-4 grid grid-cols-3 gap-1 rounded-[10px] bg-neutral-100 p-1">{sections.map(_temp2)}</TabsPrimitive.List>;
      $[50] = t21;
    } else {
      t21 = $[50];
    }
    if ($[51] !== t20) {
      t16 = <div className="shrink-0 border-b border-neutral-100 px-4 pt-4 pb-3">{t20}{t21}</div>;
      $[51] = t20;
      $[52] = t16;
    } else {
      t16 = $[52];
    }
    let t22;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <div className="mb-4 border-b border-neutral-100 pb-3"><h3 className="text-brand-700 text-sm font-semibold">Dados da negociação</h3><p className="text-subtext-color mt-1 text-xs leading-relaxed">Identificação, contato e dados personalizados.</p></div>;
      $[53] = t22;
    } else {
      t22 = $[53];
    }
    let t23;
    if ($[54] !== negotiation.numeroNegociacao) {
      t23 = negotiation.numeroNegociacao && <div className="flex flex-col gap-1"><span className="text-subtext-color text-xs">Número</span><span className="text-body-xs-medium text-default-font">{negotiation.numeroNegociacao}</span></div>;
      $[54] = negotiation.numeroNegociacao;
      $[55] = t23;
    } else {
      t23 = $[55];
    }
    let t24;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <span className="text-subtext-color text-xs">Contraparte</span>;
      $[56] = t24;
    } else {
      t24 = $[56];
    }
    let t25;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <FeatherUser className="mt-0.5 size-4 shrink-0 text-neutral-400" />;
      $[57] = t25;
    } else {
      t25 = $[57];
    }
    let t26;
    if ($[58] !== negotiation.client) {
      t26 = <div className="flex flex-col gap-1">{t24}<span className="text-default-font flex items-start gap-2 text-sm font-semibold">{t25}{negotiation.client}</span></div>;
      $[58] = negotiation.client;
      $[59] = t26;
    } else {
      t26 = $[59];
    }
    let t27;
    if ($[60] !== negotiation.counterpartyPhone || $[61] !== onCarregarTelefones || $[62] !== telefones || $[63] !== telefonesError || $[64] !== telefonesLoading) {
      t27 = <NegociacaoDetailTelefones counterpartyPhone={negotiation.counterpartyPhone} telefones={telefones} isLoading={telefonesLoading} error={telefonesError} onCarregar={onCarregarTelefones} />;
      $[60] = negotiation.counterpartyPhone;
      $[61] = onCarregarTelefones;
      $[62] = telefones;
      $[63] = telefonesError;
      $[64] = telefonesLoading;
      $[65] = t27;
    } else {
      t27 = $[65];
    }
    let t28;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <span className="text-subtext-color text-xs">CPF/CNPJ</span>;
      $[66] = t28;
    } else {
      t28 = $[66];
    }
    let t29;
    if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <FeatherUser className="size-4 shrink-0 text-neutral-400" />;
      $[67] = t29;
    } else {
      t29 = $[67];
    }
    let t30;
    if ($[68] !== negotiation.counterpartyCpfCnpj) {
      t30 = negotiation.counterpartyCpfCnpj ? formatCpfCnpj(negotiation.counterpartyCpfCnpj) : "\u2014";
      $[68] = negotiation.counterpartyCpfCnpj;
      $[69] = t30;
    } else {
      t30 = $[69];
    }
    let t31;
    if ($[70] !== t30) {
      t31 = <div className="flex flex-col gap-1">{t28}<span className="text-body-xs-medium text-default-font flex items-center gap-2">{t29}{t30}</span></div>;
      $[70] = t30;
      $[71] = t31;
    } else {
      t31 = $[71];
    }
    let t32;
    if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <span className="text-subtext-color text-xs">Criada em</span>;
      $[72] = t32;
    } else {
      t32 = $[72];
    }
    let t33;
    if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = <FeatherClock className="size-4 shrink-0 text-neutral-400" />;
      $[73] = t33;
    } else {
      t33 = $[73];
    }
    let t34;
    if ($[74] !== negotiation.createdAt) {
      t34 = <div className="flex flex-col gap-1">{t32}<span className="text-body-xs-medium text-default-font flex items-center gap-2">{t33}{negotiation.createdAt}</span></div>;
      $[74] = negotiation.createdAt;
      $[75] = t34;
    } else {
      t34 = $[75];
    }
    let t35;
    if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = <span className="text-subtext-color text-xs">Canal ativo</span>;
      $[76] = t35;
    } else {
      t35 = $[76];
    }
    let t36;
    if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
      t36 = <FeatherMessageSquare className="size-4 shrink-0 text-neutral-400" />;
      $[77] = t36;
    } else {
      t36 = $[77];
    }
    let t37;
    if ($[78] !== negotiation.channel) {
      t37 = <div className="flex flex-col gap-1">{t35}<span className="text-body-xs-medium text-default-font flex items-center gap-2">{t36}{negotiation.channel}</span></div>;
      $[78] = negotiation.channel;
      $[79] = t37;
    } else {
      t37 = $[79];
    }
    let t38;
    if ($[80] !== t23 || $[81] !== t26 || $[82] !== t27 || $[83] !== t31 || $[84] !== t34 || $[85] !== t37) {
      t38 = <div className="flex flex-col gap-4">{t23}{t26}{t27}{t31}{t34}{t37}</div>;
      $[80] = t23;
      $[81] = t26;
      $[82] = t27;
      $[83] = t31;
      $[84] = t34;
      $[85] = t37;
      $[86] = t38;
    } else {
      t38 = $[86];
    }
    let t39;
    if ($[87] !== colunasExtras || $[88] !== negotiation.customFieldValues) {
      t39 = colunasExtras.length > 0 && <section className="mt-5 flex flex-col gap-3 border-t border-neutral-100 pt-4"><h4 className="text-brand-700 text-xs font-semibold">Campos personalizados</h4>{colunasExtras.map(coluna => <div key={coluna.fieldId} className="flex flex-col gap-1"><span className="text-subtext-color text-xs">{coluna.label}</span><span className="text-body-xs-medium text-default-font break-words">{formatCustomFieldValue(negotiation.customFieldValues[coluna.fieldId], coluna.type)}</span></div>)}</section>;
      $[87] = colunasExtras;
      $[88] = negotiation.customFieldValues;
      $[89] = t39;
    } else {
      t39 = $[89];
    }
    if ($[90] !== t38 || $[91] !== t39) {
      t17 = <TabsPrimitive.Content value="dados" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">{t22}{t38}{t39}</TabsPrimitive.Content>;
      $[90] = t38;
      $[91] = t39;
      $[92] = t17;
    } else {
      t17 = $[92];
    }
    T0 = TabsPrimitive.Content;
    t11 = "atendimento";
    t12 = "flex min-h-0 flex-1 flex-col overflow-hidden";
    t8 = "min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4";
    if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="mb-4 border-b border-neutral-100 pb-3"><h3 className="text-brand-700 text-sm font-semibold">Atendimento e IA</h3><p className="text-subtext-color mt-1 text-xs leading-relaxed">Quem conduz e o estado da negociação.</p></div>;
      $[93] = t9;
    } else {
      t9 = $[93];
    }
    let t40;
    if ($[94] !== iaStatusUi.dotClass) {
      t40 = ["size-2 shrink-0 rounded-full", iaStatusUi.dotClass];
      $[94] = iaStatusUi.dotClass;
      $[95] = t40;
    } else {
      t40 = $[95];
    }
    const t41 = t40.join(" ");
    let t42;
    if ($[96] !== t41) {
      t42 = <span className={t41} />;
      $[96] = t41;
      $[97] = t42;
    } else {
      t42 = $[97];
    }
    let t43;
    if ($[98] !== iaStatusUi.title) {
      t43 = <span className="text-brand-700 text-xs font-semibold">{iaStatusUi.title}</span>;
      $[98] = iaStatusUi.title;
      $[99] = t43;
    } else {
      t43 = $[99];
    }
    let t44;
    if ($[100] !== t42 || $[101] !== t43) {
      t44 = <div className="flex items-center gap-2">{t42}{t43}</div>;
      $[100] = t42;
      $[101] = t43;
      $[102] = t44;
    } else {
      t44 = $[102];
    }
    let t45;
    if ($[103] !== iaStatusUi.message) {
      t45 = <p className="text-default-font mt-1.5 text-xs leading-relaxed">{iaStatusUi.message}</p>;
      $[103] = iaStatusUi.message;
      $[104] = t45;
    } else {
      t45 = $[104];
    }
    if ($[105] !== t44 || $[106] !== t45) {
      t10 = <div className="border-brand-100 bg-brand-50 rounded-[10px] border p-3">{t44}{t45}</div>;
      $[105] = t44;
      $[106] = t45;
      $[107] = t10;
    } else {
      t10 = $[107];
    }
    t4 = "mt-5 flex flex-col gap-4";
    let t46;
    if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
      t46 = <span className="text-subtext-color text-xs">Agente responsável</span>;
      $[108] = t46;
    } else {
      t46 = $[108];
    }
    let t47;
    if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
      t47 = <FeatherActivity className="size-4 shrink-0 text-neutral-400" />;
      $[109] = t47;
    } else {
      t47 = $[109];
    }
    if ($[110] !== negotiation.agent) {
      t5 = <div className="flex flex-col gap-1">{t46}<span className="text-body-xs-medium text-default-font flex items-center gap-2">{t47}{negotiation.agent}</span></div>;
      $[110] = negotiation.agent;
      $[111] = t5;
    } else {
      t5 = $[111];
    }
    let t48;
    if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
      t48 = <span className="text-subtext-color text-xs">Temperatura</span>;
      $[112] = t48;
    } else {
      t48 = $[112];
    }
    if ($[113] !== getTemperatureColorClass || $[114] !== negotiation.temperature) {
      t6 = <div className="flex flex-col gap-1">{t48}<NegociacaoTemperatureRow temperature={negotiation.temperature} getTemperatureColorClass={getTemperatureColorClass} /></div>;
      $[113] = getTemperatureColorClass;
      $[114] = negotiation.temperature;
      $[115] = t6;
    } else {
      t6 = $[115];
    }
    let t49;
    if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
      t49 = <span className="text-subtext-color text-xs">Confiança da IA</span>;
      $[116] = t49;
    } else {
      t49 = $[116];
    }
    const t50 = `${negotiation.confidence}%`;
    let t51;
    if ($[117] !== t50) {
      t51 = <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-neutral-100"><div className="bg-brand-600 h-full" style={{
          width: t50
        }} /></div>;
      $[117] = t50;
      $[118] = t51;
    } else {
      t51 = $[118];
    }
    let t52;
    if ($[119] !== negotiation.confidence || $[120] !== negotiation.confidenceLevel) {
      t52 = <span className="text-brand-700 shrink-0 text-xs font-semibold">{negotiation.confidence}% ({negotiation.confidenceLevel})</span>;
      $[119] = negotiation.confidence;
      $[120] = negotiation.confidenceLevel;
      $[121] = t52;
    } else {
      t52 = $[121];
    }
    if ($[122] !== t51 || $[123] !== t52) {
      t7 = <div className="flex flex-col gap-2">{t49}<div className="flex items-center gap-2">{t51}{t52}</div></div>;
      $[122] = t51;
      $[123] = t52;
      $[124] = t7;
    } else {
      t7 = $[124];
    }
    t1 = "border-t border-neutral-100 pt-4";
    if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <h4 className="text-brand-700 text-xs font-semibold">Situações da negociação</h4>;
      $[125] = t2;
    } else {
      t2 = $[125];
    }
    t3 = situacaoTags.length > 0 ? <div className="mt-2 flex flex-wrap gap-1.5">{situacaoTags.map(_temp3)}</div> : <p className="text-subtext-color mt-2 text-xs">Nenhuma situação registrada.</p>;
    $[0] = colunasExtras;
    $[1] = getTemperatureColorClass;
    $[2] = iaStatusUi.dotClass;
    $[3] = iaStatusUi.message;
    $[4] = iaStatusUi.title;
    $[5] = negotiation.agent;
    $[6] = negotiation.channel;
    $[7] = negotiation.client;
    $[8] = negotiation.confidence;
    $[9] = negotiation.confidenceLevel;
    $[10] = negotiation.counterpartyCpfCnpj;
    $[11] = negotiation.counterpartyPhone;
    $[12] = negotiation.createdAt;
    $[13] = negotiation.customFieldValues;
    $[14] = negotiation.negotiationName;
    $[15] = negotiation.numeroNegociacao;
    $[16] = negotiation.status;
    $[17] = negotiation.statusVariant;
    $[18] = negotiation.temperature;
    $[19] = onCarregarTelefones;
    $[20] = onSectionChange;
    $[21] = section;
    $[22] = situacoes;
    $[23] = telefones;
    $[24] = telefonesError;
    $[25] = telefonesLoading;
    $[26] = T0;
    $[27] = T1;
    $[28] = t1;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
    $[36] = t17;
    $[37] = t2;
    $[38] = t3;
    $[39] = t4;
    $[40] = t5;
    $[41] = t6;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
  } else {
    T0 = $[26];
    T1 = $[27];
    t1 = $[28];
    t10 = $[29];
    t11 = $[30];
    t12 = $[31];
    t13 = $[32];
    t14 = $[33];
    t15 = $[34];
    t16 = $[35];
    t17 = $[36];
    t2 = $[37];
    t3 = $[38];
    t4 = $[39];
    t5 = $[40];
    t6 = $[41];
    t7 = $[42];
    t8 = $[43];
    t9 = $[44];
  }
  let t18;
  if ($[126] !== t1 || $[127] !== t2 || $[128] !== t3) {
    t18 = <section className={t1}>{t2}{t3}</section>;
    $[126] = t1;
    $[127] = t2;
    $[128] = t3;
    $[129] = t18;
  } else {
    t18 = $[129];
  }
  let t19;
  if ($[130] !== t18 || $[131] !== t4 || $[132] !== t5 || $[133] !== t6 || $[134] !== t7) {
    t19 = <div className={t4}>{t5}{t6}{t7}{t18}</div>;
    $[130] = t18;
    $[131] = t4;
    $[132] = t5;
    $[133] = t6;
    $[134] = t7;
    $[135] = t19;
  } else {
    t19 = $[135];
  }
  let t20;
  if ($[136] !== t10 || $[137] !== t19 || $[138] !== t8 || $[139] !== t9) {
    t20 = <div className={t8}>{t9}{t10}{t19}</div>;
    $[136] = t10;
    $[137] = t19;
    $[138] = t8;
    $[139] = t9;
    $[140] = t20;
  } else {
    t20 = $[140];
  }
  let t21;
  if ($[141] !== canToggleAutomation || $[142] !== isIAPaused || $[143] !== isReadOnly || $[144] !== isStartCancelled || $[145] !== isStartFailed || $[146] !== isStartPending || $[147] !== isToggleAutomationLoading || $[148] !== onToggleAutomationClick) {
    t21 = !isReadOnly && <div className="shrink-0 border-t border-neutral-100 bg-white p-4"><Button variant="brand-primary" size="small" className="w-full" onClick={onToggleAutomationClick} disabled={!canToggleAutomation || isToggleAutomationLoading} loading={isToggleAutomationLoading}>{isIAPaused ? "Retomar Automa\xE7\xE3o" : "Assumir Manualmente"}</Button>{!isIAPaused && canToggleAutomation && !isStartPending && !isStartFailed && !isStartCancelled ? <p className="text-subtext-color mt-2 text-center text-[10px] leading-relaxed">A IA responderá automaticamente à próxima interação</p> : null}{!canToggleAutomation && <p className="text-subtext-color mt-2 text-center text-[10px]">Conversa indisponível no momento.</p>}</div>;
    $[141] = canToggleAutomation;
    $[142] = isIAPaused;
    $[143] = isReadOnly;
    $[144] = isStartCancelled;
    $[145] = isStartFailed;
    $[146] = isStartPending;
    $[147] = isToggleAutomationLoading;
    $[148] = onToggleAutomationClick;
    $[149] = t21;
  } else {
    t21 = $[149];
  }
  let t22;
  if ($[150] !== T0 || $[151] !== t11 || $[152] !== t12 || $[153] !== t20 || $[154] !== t21) {
    t22 = <T0 value={t11} className={t12}>{t20}{t21}</T0>;
    $[150] = T0;
    $[151] = t11;
    $[152] = t12;
    $[153] = t20;
    $[154] = t21;
    $[155] = t22;
  } else {
    t22 = $[155];
  }
  let t23;
  if ($[156] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="mb-4 border-b border-neutral-100 pb-3"><h3 className="text-brand-700 text-sm font-semibold">Como a IA atua</h3><p className="text-subtext-color mt-1 text-xs leading-relaxed">Fluxo, parâmetros e etapas configuradas.</p></div>;
    $[156] = t23;
  } else {
    t23 = $[156];
  }
  let t24;
  if ($[157] !== agentInput || $[158] !== flow || $[159] !== isFlowLoading) {
    t24 = <TabsPrimitive.Content value="automacao" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">{t23}<NegociacaoDetailFlowSection flow={flow} isFlowLoading={isFlowLoading} agentInput={agentInput} presentation="sections" /></TabsPrimitive.Content>;
    $[157] = agentInput;
    $[158] = flow;
    $[159] = isFlowLoading;
    $[160] = t24;
  } else {
    t24 = $[160];
  }
  let t25;
  if ($[161] !== T1 || $[162] !== t13 || $[163] !== t14 || $[164] !== t15 || $[165] !== t16 || $[166] !== t17 || $[167] !== t22 || $[168] !== t24) {
    t25 = <T1 value={t13} onValueChange={t14} className={t15}>{t16}{t17}{t22}{t24}</T1>;
    $[161] = T1;
    $[162] = t13;
    $[163] = t14;
    $[164] = t15;
    $[165] = t16;
    $[166] = t17;
    $[167] = t22;
    $[168] = t24;
    $[169] = t25;
  } else {
    t25 = $[169];
  }
  return t25;
}
function _temp3(tag_0) {
  return <span key={tag_0.id} className="neg-situacao-pill">{tag_0.tone === "warning" && <span className="neg-situacao-pill__dot" />}{tag_0.label}</span>;
}
function _temp2(t0) {
  const {
    value,
    label,
    icon: Icon
  } = t0;
  return <TabsPrimitive.Trigger key={value} value={value} className="text-subtext-color hover:text-brand-700 focus-visible:outline-brand-600 data-[state=active]:border-brand-100 data-[state=active]:text-brand-700 flex min-w-0 flex-col items-center justify-center gap-1 rounded-[8px] border border-transparent px-0.5 py-2 text-[11px] leading-tight font-medium transition-colors focus-visible:outline-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"><Icon className="size-4" strokeWidth={1.75} aria-hidden="true" /><span>{label}</span></TabsPrimitive.Trigger>;
}
function _temp(tag) {
  return <span key={tag.id} className="neg-situacao-pill">{tag.tone === "warning" && <span className="neg-situacao-pill__dot" />}{tag.label}</span>;
}